import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toastTheme.css';
import { exportFilteredComplaints, exportFilteredMaintenanceHistory } from './utils/exportUtils';
import { setupAxiosInterceptors } from './utils/axiosConfig';
import { useRoleAccess } from './hooks/useRoleAccess';
import { useSessionPoller } from './features/auth/hooks';
import { AuthProvider } from './features/auth/context';
import { API_ENDPOINTS } from './config/apiConfig';
import { ACTIVE_COMPLAINT_STATUSES } from './utils/fleetMetricUtils';
import { applyUiPreferencesFromUser, getLandingView, getLoginAlertsToast } from './shared/utils/uiPreferences';
import {
  clearPostLoginView,
  consumePostLoginView,
  pathToView,
  resolveDeepLinkView,
  resolveInitialAppView,
  stashPostLoginViewFromPathname,
  viewToPath,
} from './shared/utils/navDeepLink';
import './App.css';
import Login from './pages/Login/Login';
import Devices from './pages/Devices/DevicesPage';
import AdminDashboard from './features/dashboard/pages/AdminDashboard';
import TechnicianDashboard from './features/dashboard/pages/TechnicianDashboard';
import ManagerDashboard from './features/dashboard/pages/ManagerDashboard';
import CentralHelpDeskDashboard from './features/dashboard/pages/CentralHelpDeskDashboard';
import CustodianDashboard from './features/dashboard/pages/CustodianDashboard';
import CoordinatorDashboard from './features/dashboard/pages/CoordinatorDashboard';
import Users from './pages/Users/UsersPage';
import SettingsPage from './pages/Settings/SettingsPage';
import AuditViewerPage from './pages/Audit/AuditViewerPage';
import SuperAdminDashboard from './features/dashboard/pages/SuperAdminDashboard';
import LocationsManagement from './pages/Locations/LocationsPage';
import DepartmentManagement from './pages/Departments/DepartmentManagementPage';
import { DeviceNameManagement } from './features/devices/components';
import { ComplaintCategoryManagement } from './features/complaints/components';
import { SuperAdminAnalytics, AdminAnalytics } from './features/analytics/components';
import DataImportPage from './pages/DataImport/DataImportPage';
import { ProfileSettings } from './features/auth/components';
import PreferencesPage from './features/auth/components/PreferencesPage';
import { HelpPage } from './features/help';
import Logo from './shared/components/common/Logo';
import { TechnicianAnalytics } from './features/analytics/components';
import { Alerts } from './features/alerts/components';
import Complaints from './pages/Complaints/ComplaintsPage';
import MyWorkLogs from './shared/components/common/MyWorkLogs';
import ActivityLogPage from './pages/ActivityLog/ActivityLogPage';
import { DeviceMaintenanceHistory } from './features/devices/components';
import AssignedDevices from './shared/components/common/AssignedDevices';
import DeviceApprovals from './pages/Approvals/DeviceApprovalsPage';
import LocationTransfersPage from './pages/Transfers/LocationTransfersPage';
import DepartmentTransfersPage from './pages/DepartmentTransfers/DepartmentTransfersPage';
import AppShell from './shared/components/layouts/AppShell';
import { buildNavGroups } from './shared/components/layouts/navConfig';
import GuestDevicePage from './shared/components/common/GuestDevicePage';
import GuestComplaintConfirmation from './shared/components/common/GuestComplaintConfirmation';
import LogoutModal from './features/auth/components/LogoutModal';
import KickedOutPage from './pages/KickedOut/KickedOutPage';
import PasswordResetRequired from './pages/ResetPassword/PasswordResetRequired';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import ReactivateAccount from './pages/Reactivation/ReactivateAccount';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import SystemControlsPage from './pages/Settings/SystemControlsPage';
import OrganizationSettingsPage from './pages/Settings/OrganizationSettingsPage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/Legal/TermsOfServicePage';
import SupportPage from './pages/Legal/SupportPage';
import './styles/filters-ui.css';

// Helper function to detect browser mode (normal vs incognito/private)
// Multi-method approach: localStorage write test + size quota test
const detectBrowserMode = async () => {
  try {
    // Method 1: Try to write to localStorage (fails in Firefox/Safari private)
    try {
      const testKey = '__mode_detect_' + Date.now();
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);

      if (retrieved !== testValue) {
        return 'incognito';
      }
    } catch (e) {
      // If localStorage throws, definitely incognito (Firefox Private, Safari Private)
      return 'incognito';
    }

    // Method 2: Progressive storage quota test
    // Normal mode: Can store 5-10MB+
    // Incognito: Usually quota exceeded around 5-10MB
    try {
      const testSize = 1 * 1024 * 1024; // 1MB
      const testData = new Array(testSize).fill('x').join('');
      localStorage.setItem('__quota_test_1mb__', testData);
      localStorage.removeItem('__quota_test_1mb__');

      // Try 5MB
      try {
        const testSize5 = 5 * 1024 * 1024; // 5MB
        const testData5 = new Array(testSize5).fill('x').join('');
        localStorage.setItem('__quota_test_5mb__', testData5);
        localStorage.removeItem('__quota_test_5mb__');
        return 'normal';
      } catch (e5) {
        if (e5.name === 'QuotaExceededError') {
          return 'incognito';
        }
        throw e5; // Re-throw if different error
      }
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        return 'incognito';
      }
      return 'normal';
    }
  } catch (e) {
    console.warn('Unexpected error in browser mode detection:', e);
    return 'normal'; // Safe fallback
  }
};

// Component to handle device viewing via QR code - SIMPLIFIED
// Route: /device-scan?token={token} where token = base64(org_id|device_identifier)
function QRDeviceViewHandler() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [device, setDevice] = useState(null);

  useEffect(() => {
    const validateAndLoadDevice = async () => {
      try {
        // Extract token from query parameter: /device-scan?token={token}
        const searchParams = new URLSearchParams(window.location.search);
        const token = searchParams.get('token'); // Get token from query parameter

        // Token is REQUIRED - this is the ONLY way to access QR device information
        if (!token) {
          setValidationError('Invalid QR code. No token provided.');
          setIsInitialized(true);
          return;
        }

        // Store the QR token in sessionStorage so we can redirect back to it on "Report Another"
        sessionStorage.setItem('qr_device_token', token);

        // SECURITY: Call /api/devices/qr/{token}
        // Backend decodes base64 token to extract org_id|device_identifier and validates org ownership
        const response = await axios.get(API_ENDPOINTS.DEVICES.QR_DEVICE(token));

        if (response.status === 200 && response.data.device_identifier) {
          setDevice(response.data);

          // Store guest device data in sessionStorage for GuestComplaintConfirmation to access organization_id
          sessionStorage.setItem('guest_device_data', JSON.stringify(response.data));
        }
      } catch (error) {
        // Handle errors from axios
        if (error.response?.status === 404) {
          console.error('Device not found');
          setValidationError('Device not found. The link may be invalid or the device has been removed.');
        } else if (error.response?.status === 400) {
          // Invalid/tampered QR code
          console.error('Invalid QR code:', error.response.data);
          setValidationError('Invalid QR code. The link may have been modified, is corrupted, or is no longer valid.');
        } else if (error.response?.status === 500) {
          // Server configuration error
          console.error('Server error:', error.response.data);
          setValidationError('System configuration error. QR code system is not properly configured.');
        } else {
          console.error('API error:', error);
          setValidationError('Failed to load device information. Please try again.');
        }
      } finally {
        setIsInitialized(true);
      }
    };

    validateAndLoadDevice();
  }, []);

  // Show error if validation failed
  if (isInitialized && (!device || validationError)) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-accent-gradient)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#d32f2f', margin: '0 0 1rem 0' }}>Invalid Link</h2>
          <p style={{ color: '#666', margin: 0 }}>
            {validationError || 'No device information available. Please scan a valid QR code.'}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-accent-gradient)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Device loaded - show guest device page with device data
  return (
    <GuestDevicePage
      device={device}
      onSuccess={(result) => {
        // Store complaint ID and redirect to confirmation page
        const complaintId = result.complaint_identifier;
        sessionStorage.setItem('complaint_id', complaintId);
        sessionStorage.setItem('reporter_full_name', result.reporter_full_name || '');
        sessionStorage.setItem('reporter_email', result.reporter_email || '');
        sessionStorage.setItem('reporter_contact_number', result.reporter_contact_number || '');
        sessionStorage.setItem('reporter_role', result.reporter_role || '');
        sessionStorage.setItem('assigned_employee_id', result.assigned_employee_id || '');
        sessionStorage.setItem('assigned_employee_name', result.assigned_employee_name || '');
        sessionStorage.setItem('assigned_employee_email', result.assigned_employee_email || '');
        sessionStorage.setItem('assigned_employee_contact', result.assigned_employee_contact || '');
        sessionStorage.setItem('assigned_employee_role', result.assigned_employee_role || '');
        if (result.photoUploadWarning) {
          sessionStorage.setItem('photo_upload_warning', result.photoUploadWarning);
        } else {
          sessionStorage.removeItem('photo_upload_warning');
        }
        if (result.attachedPhotos?.length) {
          sessionStorage.setItem('complaint_photo_previews', JSON.stringify(result.attachedPhotos));
        } else {
          sessionStorage.removeItem('complaint_photo_previews');
        }
        window.location.href = '/complaint-confirmation';
      }}
    />
  );
}

function GlobalToastContainer() {
  // Render toasts at document.body level so they always appear above portal modals.
  return ReactDOM.createPortal(
    <ToastContainer
      position="bottom-center"
      autoClose={2500}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable={false}
      pauseOnHover
      transition={Slide}
      theme="light"
      limit={3}
      style={{ zIndex: 999999 }}
    />,
    document.body
  );
}

// Component to handle complaint confirmation page
function ComplaintConfirmationHandler() {
  const [complaintId, setComplaintId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Get complaint ID from sessionStorage (set by QRDeviceViewHandler after complaint submission)
    const storedComplaintId = sessionStorage.getItem('complaint_id');

    if (storedComplaintId) {
      setComplaintId(storedComplaintId);
    }

    setIsInitialized(true);
  }, []);

  // Show error if no complaint ID found
  if (isInitialized && !complaintId) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-accent-gradient)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#d32f2f', margin: '0 0 1rem 0' }}>Invalid Link</h2>
          <p style={{ color: '#666', margin: 0 }}>No complaint reference ID found. Please scan a valid QR code.</p>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-accent-gradient)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <GuestComplaintConfirmation
      complaintId={complaintId}
      onLogAnother={() => {
        // Clear the current complaint and redirect back to scan another QR
        sessionStorage.removeItem('complaint_id');
        sessionStorage.removeItem('reporter_full_name');
        sessionStorage.removeItem('reporter_email');
        sessionStorage.removeItem('reporter_contact_number');
        sessionStorage.removeItem('reporter_role');
        sessionStorage.removeItem('assigned_employee_id');
        sessionStorage.removeItem('assigned_employee_name');
        sessionStorage.removeItem('assigned_employee_email');
        sessionStorage.removeItem('assigned_employee_contact');
        sessionStorage.removeItem('assigned_employee_role');
        sessionStorage.removeItem('photo_upload_warning');
        sessionStorage.removeItem('complaint_photo_previews');
        sessionStorage.removeItem('guest_device_data');
        // Retrieve the stored QR token and redirect back to the device page
        const qrToken = sessionStorage.getItem('qr_device_token');
        if (qrToken) {
          // Redirect back to the original QR device page to allow reporting another complaint
          // Token is already base64-encoded, safe for use in query parameter
          window.location.href = `/device-scan?token=${qrToken}`;
        } else {
          // Fallback: if no token is stored, redirect to home
          window.location.href = window.location.origin;
        }
      }}
    />
  );
}

function App() {
  const isLoggingOutRef = useRef(false); // Guard flag to prevent logout loop
  const handleLogoutRef = useRef(null); // TIER 3: Ref to store handleLogout for polling
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Mobile menu state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [stats, setStats] = useState(null);
  const [criticalComplaints, setCriticalComplaints] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');
  const [settingsSection, setSettingsSection] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Set navbar / cross-nav view and sync the URL.
   * Pushes history so browser Back works between deep-linked nav pages.
   * Pass { replace: true } for redirects that should not add a history entry
   * (e.g. technician Devices → My Devices). Auth entry/logout use replace separately.
   */
  const navigateToView = useCallback((view, options = {}) => {
    setCurrentView(view);
    const path = viewToPath(view);
    if (path && window.location.pathname !== path) {
      navigate(path, { replace: Boolean(options.replace) });
    }
    // Reset window scroll so handoffs from deep dashboard sections land at page top
    // (also covers same-view re-nav with new filter/sort context).
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [navigate]);

  const navigateToLocationTransfers = (nav) => {
    if (nav?.direction) {
      sessionStorage.setItem('transferDirection', nav.direction);
    }
    if (nav?.statusTab) {
      sessionStorage.setItem('transferStatusTab', nav.statusTab);
    }
    navigateToView('location-transfers');
  };
  const navigateToDepartmentTransfers = (nav) => {
    if (nav?.direction) {
      sessionStorage.setItem('departmentTransferDirection', nav.direction);
    }
    if (nav?.statusTab) {
      sessionStorage.setItem('departmentTransferStatusTab', nav.statusTab);
    }
    navigateToView('department-transfers');
  };
  const navigateToSettings = (section = null) => {
    setSettingsSection(section);
    navigateToView('settings');
  };
  const navigateToUsers = (statusGroup = null, locationIds = null) => {
    setUsersStatusGroupFilter(statusGroup);
    setUsersLocationFilter(Array.isArray(locationIds) && locationIds.length > 0 ? locationIds : null);
    navigateToView('users');
  };
  const [alertCount, setAlertCount] = useState(0);
  const [developerSettingsAvailable, setDeveloperSettingsAvailable] = useState(false);
  const [systemControlsAvailable, setSystemControlsAvailable] = useState(false);
  const [viewingComplaintId, setViewingComplaintId] = useState(null);
  const [complaintStatusFilter, setComplaintStatusFilter] = useState(null); // Track complaint filter for navigation
  const [complaintSeverityFilter, setComplaintSeverityFilter] = useState(null); // Track complaint severity filter for navigation
  const [complaintDaysOpenFromFilter, setComplaintDaysOpenFromFilter] = useState(null); // Track complaint days open from filter for navigation
  const [complaintDaysOpenToFilter, setComplaintDaysOpenToFilter] = useState(null); // Track complaint days open to filter for navigation
  const [complaintDeviceIdsFilter, setComplaintDeviceIdsFilter] = useState(null); // Track complaint device IDs filter for navigation
  const [complaintSearchFilter, setComplaintSearchFilter] = useState(null); // Track complaint search text for navigation (e.g. device ID from Device View)
  const [maintenanceSearchFilter, setMaintenanceSearchFilter] = useState(null); // Device identifier search when navigating to maintenance history
  const [usersStatusGroupFilter, setUsersStatusGroupFilter] = useState(null); // statusGroup when navigating from Admin dashboard
  const [usersLocationFilter, setUsersLocationFilter] = useState(null);
  // TIER 4: Logout modal state
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Device view state (for backward compatibility with dashboard callbacks)
  // Note: The Devices component manages its own internal state
  const [viewingDeviceId, setViewingDeviceId] = useState(null);
  const [showDeviceView, setShowDeviceView] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceViewSource, setDeviceViewSource] = useState('devices');
  // Alert modal state - track when device view is open in Alerts
  const [alertViewingDeviceId, setAlertViewingDeviceId] = useState(null);
  const [alertShowDeviceModal, setAlertShowDeviceModal] = useState(false);
  // Track logout reason for display on kicked-out page
  const [kickedOutReason, setKickedOutReason] = useState(null);

  // Browser Back/Forward: URL changes via React Router — sync currentView to match.
  // Do not navigate() here (path is already correct). Skip when path is not a nav deep link.
  useEffect(() => {
    if (!user || !token || user.password_reset_required) return;

    const pathView = pathToView(location.pathname);

    // Technicians only use /devices for device detail/edit. Do not remap to My Devices
    // or clear detail state while a device view/form is open (e.g. Complaint → Full Details).
    if (
      user.role === 'technician' &&
      pathView === 'devices' &&
      (showDeviceView || showForm || viewingDeviceId)
    ) {
      if (currentView !== 'devices') {
        setCurrentView('devices');
      }
      return;
    }

    const viewFromPath = resolveDeepLinkView(location.pathname, user.role);
    if (!viewFromPath || viewFromPath === currentView) return;

    setCurrentView(viewFromPath);
    // Land on the nav page itself (not a stale device/complaint detail from a prior visit)
    setViewingDeviceId(null);
    setViewingComplaintId(null);
    setShowDeviceView(false);
    setShowForm(false);
    setSelectedDevice(null);
    setAlertShowDeviceModal(false);
    setAlertViewingDeviceId(null);
    // currentView intentionally omitted from deps — only react to URL / auth / device-detail flags
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user, token, showDeviceView, showForm, viewingDeviceId]);

  // TIER 3: Setup session polling for cross-mode logout detection
  // Pass the ref directly to avoid creating a new function on every render
  // The hook will call handleLogoutRef.current when session is invalidated
  useSessionPoller(token, handleLogoutRef);

  // Initialize handleLogoutRef with the handleLogout function
  // This must run BEFORE polling attempts to call it
  // It will be called on every render, but that's fine since we're just storing a reference
  useEffect(() => {
    if (!handleLogoutRef.current) {
      handleLogoutRef.current = handleLogout;
    }
  }, []);

  // Skip session-close beacon on keyboard refresh (F5 / Ctrl+R / Cmd+R).
  // beforeunload also fires on refresh; browser-toolbar refresh is handled via
  // backend SESSION_REFRESH_GRACE_SECONDS reactivation in /session/check.
  useEffect(() => {
    const markRefreshIntent = (event) => {
      const key = event.key?.toLowerCase();
      if (
        key === 'f5'
        || ((event.ctrlKey || event.metaKey) && key === 'r')
      ) {
        sessionStorage.setItem('skip_session_close_beacon', '1');
      }
    };
    window.addEventListener('keydown', markRefreshIntent);
    return () => window.removeEventListener('keydown', markRefreshIntent);
  }, []);

  // Close session on tab/window close (not refresh) via sendBeacon.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStorage.getItem('skip_session_close_beacon') === '1') {
        sessionStorage.removeItem('skip_session_close_beacon');
        return;
      }

      const sessionId = sessionStorage.getItem('session_id');

      if (sessionId) {
        try {
          const formData = new URLSearchParams();
          formData.append('session_id', sessionId);

          navigator.sendBeacon(
            API_ENDPOINTS.AUTH.SESSION_CLOSE,
            formData
          );
        } catch (error) {
          console.error('[BEFOREUNLOAD] Error sending session close beacon:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu and reset page scroll when the primary view changes
  // (covers navigateToView, direct setCurrentView, and URL/back-forward sync).
  useLayoutEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentView, setMobileMenuOpen]);

  // Check if Developer Settings feature is enabled in this environment.
  // Backend is the source of truth: if DEVELOPER_SETTINGS_ENABLED is off, endpoint returns 403.
  // This endpoint also requires superadmin; treat non-200 as "not available" for navbar display.
  useEffect(() => {
    const checkDeveloperSettingsAvailability = async () => {
      if (!token || !user || user.role !== 'superadmin') {
        setDeveloperSettingsAvailable(false);
        return;
      }

      try {
        const res = await fetch(API_ENDPOINTS.DEV_SETTINGS.GET, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setDeveloperSettingsAvailable(res.ok);
      } catch (e) {
        setDeveloperSettingsAvailable(false);
      }
    };

    checkDeveloperSettingsAvailability();
  }, [token, user?.role]);

  // Check if System Controls feature is enabled in this environment.
  // Backend is the source of truth: if SYSTEM_CONTROLS_ENABLED is off, endpoint returns 403.
  // This endpoint also requires superadmin; treat non-200 as "not available" for navbar display.
  useEffect(() => {
    const checkSystemControlsAvailability = async () => {
      if (!token || !user || user.role !== 'superadmin') {
        setSystemControlsAvailable(false);
        return;
      }

      try {
        // Avoid showing stale availability when backend flag flips at runtime.
        setSystemControlsAvailable(false);
        const res = await fetch(API_ENDPOINTS.SYSTEM_CONTROLS.GET, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSystemControlsAvailable(res.ok);
      } catch (e) {
        setSystemControlsAvailable(false);
      }
    };

    checkSystemControlsAvailability();
  }, [token, user?.role, currentView]);

  // Check for existing session on mount
  useEffect(() => {
    // Check if user was kicked out - restore reason from sessionStorage
    const storedKickedOutReason = sessionStorage.getItem('kicked_out_reason');
    if (storedKickedOutReason) {
      setKickedOutReason(storedKickedOutReason);
    }

    const storedToken = sessionStorage.getItem('token');
    const storedUser = sessionStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);

        // Validate token format (JWT should have 3 parts separated by dots)
        const isValidTokenFormat = storedToken.split('.').length === 3;

        if (isValidTokenFormat && userData && userData.id) {
          const storedLocId = sessionStorage.getItem('location_id');
          const storedLocName = sessionStorage.getItem('location_name');
          const storedOrgName = sessionStorage.getItem('organization_name');

          setToken(storedToken);
          setUser(userData);
          applyUiPreferencesFromUser(userData);
          // Use location_id from userData as fallback if not in localStorage
          setLocationId(storedLocId ? parseInt(storedLocId) : (userData.location_id ? parseInt(userData.location_id) : null));
          setLocationName(storedLocName || '');
          setOrganizationName(storedOrgName || '');

          // Refresh user from /me so department assignments stay current (multi-dept navbar)
          axios.get(API_ENDPOINTS.AUTH.ME, {
            headers: { Authorization: `Bearer ${storedToken}` },
          }).then((response) => {
            const freshUser = response.data;
            setUser(freshUser);
            sessionStorage.setItem('user', JSON.stringify(freshUser));
            applyUiPreferencesFromUser(freshUser);
            if (freshUser.location_name) {
              setLocationName(freshUser.location_name);
              sessionStorage.setItem('location_name', freshUser.location_name);
            }
            if (freshUser.location_id) {
              setLocationId(parseInt(freshUser.location_id, 10));
              sessionStorage.setItem('location_id', freshUser.location_id);
            }
            if (freshUser.organization_name) {
              setOrganizationName(freshUser.organization_name);
              sessionStorage.setItem('organization_name', freshUser.organization_name);
            }
          }).catch((error) => {
            console.error('Error refreshing user session:', error);
          });

          // Deep link path (or post-login stash) wins over preferred landing page
          if (!userData.password_reset_required) {
            const nextView = resolveInitialAppView({
              pathname: window.location.pathname,
              role: userData.role,
              preferredLanding:
                userData.ui_preferences?.landing_view || getLandingView(),
              postLoginViewId: consumePostLoginView(),
            });
            setCurrentView(nextView);
            const path = viewToPath(nextView);
            if (path && window.location.pathname !== path) {
              navigate(path, { replace: true });
            }
          }
        } else {
          // Token format is invalid, clear it
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Clear corrupted data
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('location_id');
        sessionStorage.removeItem('location_name');
        sessionStorage.removeItem('organization_name');
      }
    }
  }, []);

  // TIER 2: Setup axios interceptor for 401 response handling
  // This runs whenever token changes (login, logout, session restore)
  useEffect(() => {
    if (token && user) {
      // Token is available - set up the interceptor to handle 401 responses
      // Note: We don't include handleLogout in dependencies to avoid circular reference
      // The setupAxiosInterceptors will receive handleLogout and performLogout as parameters when called
      setupAxiosInterceptors(
        () => {
          // Legacy handleLogout callback
          if (typeof handleLogout === 'function') {
            handleLogout();
          }
        },
        // Pass performLogout so interceptor can pass reason codes
        performLogout
      );
    }
  }, [token, user]);

  // TIER 1: Cross-Tab Logout - Listen for logout signals from other tabs
  const lastLogoutSignalRef = useRef(null);
  useEffect(() => {
    const handleLogoutSignal = (e) => {
      // Check if this is a logout signal for the current user
      if (e.key && e.key.startsWith('logout_signal_')) {
        // Extract user ID from the key: logout_signal_USERID
        const logoutUserId = e.key.replace('logout_signal_', '');

        // If this logout signal is for the current user, logout this tab too
        if (user && user.id && user.id.toString() === logoutUserId) {
          // Prevent duplicate logout signals (debounce)
          const now = Date.now();
          if (lastLogoutSignalRef.current && (now - lastLogoutSignalRef.current) < 500) {
            return;
          }
          lastLogoutSignalRef.current = now;

          // Set kicked-out reason so user sees the appropriate message
          setKickedOutReason('logged_out_other_device');

          // Clear session storage
          try {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('location_id');
            sessionStorage.removeItem('location_name');
            sessionStorage.removeItem('organization_name');
            sessionStorage.removeItem('session_created_at');
            sessionStorage.removeItem('browser_mode');
            sessionStorage.clear();
          } catch (e) {
            console.warn('Failed to clear sessionStorage:', e.message);
          }

          // Clear auth state - this prevents CRUD operations
          setUser(null);
          setToken(null);
          setLocationId(null);
          setLocationName('');
          setOrganizationName('');
          setCurrentView('login');
          setShowLogoutModal(false);
          if (window.location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }

          // Show notification to user (only once per 500ms)
          toast.warning('You have been logged out from another tab');
        }
      }
    };

    // Add event listener for storage changes (from other tabs)
    window.addEventListener('storage', handleLogoutSignal);

    // Cleanup listener on unmount
    return () => window.removeEventListener('storage', handleLogoutSignal);
  }, [user, navigate]);

  // Set organization name from login response (now included in user object)
  // OPTIMIZATION: Organization name is included in login response, no additional API call needed
  useEffect(() => {
    if (user && user.organization_name && !organizationName) {
      // Organization name is available from login response
      setOrganizationName(user.organization_name);
      sessionStorage.setItem('organization_name', user.organization_name);
    }
  }, [user?.organization_name, organizationName]);

  // Fetch location name if missing but user has a location
  useEffect(() => {
    if (user && token && user.location_id && !locationName) {
      axios.get(API_ENDPOINTS.LOCATIONS.GET(user.location_id), {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(response => {
        if (response.data && response.data.name) {
          setLocationName(response.data.name);
          sessionStorage.setItem('location_name', response.data.name);
        }
      }).catch(error => {
        console.error('Error fetching location name in useEffect:', error);
      });
    }
  }, [user?.location_id, token]);

  // Fetch data when authenticated
  useEffect(() => {
    if (token) {
      // Always fetch alert count for superadmin (regardless of currentView)
      if (user?.role === 'superadmin') {
        fetchAlertCount();
      }
      // For other roles, fetch data when not on admin/settings/superadmin views
      else if (currentView !== 'admin' && currentView !== 'settings' && currentView !== 'superadmin') {
        // Skip stats fetch for Technicians (no analytics access)
        if (user?.role !== 'technician') {
          fetchStats();
        } else {
          // For Technicians, still fetch critical complaints for dashboard
          fetchCriticalComplaints();
        }
        fetchAlertCount();
      }
    }
  }, [token, currentView, user?.role]);

  // Reset sub-page state when navigating via navbar
  // IMPORTANT: Only reset if NOT already navigating to a specific device/view
  useEffect(() => {
    if (currentView === 'devices') {
      // Only reset view if we're NOT trying to view a specific device
      // (viewingDeviceId being set means we're navigating TO a device view, not just to list)
      if (!viewingDeviceId) {
        // Ensure we show device list, not detail view
        // When clicking Devices navbar, always show fresh list (not detail or form)
        setShowDeviceView(false);
        setShowForm(false);
        setSelectedDevice(null);
      }
    } else if (currentView === 'complaints') {
      // NOTE: Complaint filters are reset by the navbar button directly (lines 1374-1375)
      // We don't reset them here to avoid interfering with dashboard filter navigation
      // The navbar onClick handler calls setComplaintStatusFilter(null) and setComplaintSeverityFilter(null)
      // This prevents the useEffect from overwriting filters set by handleNavigateToComplaintsWithFilter
    } else if (currentView === 'maintenance-history') {
      // Device search filter (if any) comes from maintenanceSearchFilter via flushSync navigation
    }
  }, [currentView, viewingDeviceId, viewingComplaintId]);


  const getAuthHeaders = () => ({
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  const handleLogin = async (userData, authToken) => {
    // Clear any previous kicked-out reason when user logs back in
    sessionStorage.removeItem('kicked_out_reason');
    setKickedOutReason(null);

    // Update App.js local state
    setUser(userData);
    setToken(authToken);
    setLocationId(userData.location_id);
    // Persist user before applying prefs so AppShell can read account sidebar_mode on mount
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('token', authToken);
    applyUiPreferencesFromUser(userData);

    // TIER 3: Ensure ref is set even before handleLogout is called
    // This is a temporary reference that will be properly set when handleLogout is first called
    if (!handleLogoutRef.current) {
      handleLogoutRef.current = handleLogout;
    }

    // Note: Axios interceptor and session poller will be configured in useEffects

    // TIER 3: Detect browser mode for session tracking
    const browserMode = await detectBrowserMode();

    // Store browser mode in sessionStorage for reference
    sessionStorage.setItem('browser_mode', browserMode);

    // TIER 3: Use session_id returned from backend (created during login)
    // The backend generates and returns the session_id to ensure consistency
    // If backend didn't return session_id (for backward compatibility), don't set it
    if (authToken && authToken.session_id) {
      sessionStorage.setItem('session_id', authToken.session_id);
    }

    // Save user object and token to sessionStorage (clears on browser close)
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('token', authToken);
    sessionStorage.setItem('session_created_at', new Date().toISOString());

    // Save location_id to sessionStorage
    if (userData.location_id) {
      sessionStorage.setItem('location_id', userData.location_id);
    }

    // Deep link / post-login stash wins over preferred landing page
    if (!userData.password_reset_required) {
      const nextView = resolveInitialAppView({
        pathname: window.location.pathname,
        role: userData.role,
        preferredLanding:
          userData.ui_preferences?.landing_view || getLandingView(),
        postLoginViewId: consumePostLoginView(),
      });
      setCurrentView(nextView);
      const path = viewToPath(nextView);
      if (path && window.location.pathname !== path) {
        navigate(path, { replace: true });
      }
    }

    // Fetch organization name using organization_id from login response
    // PHASE P0: Dynamic organization context (no hardcoded IDs)
    if (userData.organization_id) {
      try {
        const orgResponse = await axios.get(API_ENDPOINTS.ORGANIZATIONS.GET(userData.organization_id), {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        if (orgResponse.data && orgResponse.data.name) {
          setOrganizationName(orgResponse.data.name);
          sessionStorage.setItem('organization_name', orgResponse.data.name);
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
        // Continue anyway, just won't show organization name
      }
    }

    // Set location name from login response if available
    if (userData.location_name) {
      setLocationName(userData.location_name);
      sessionStorage.setItem('location_name', userData.location_name);
    } else if (userData.location_id) {
      // Fallback: fetch location name if not in login response
      try {
        const response = await axios.get(API_ENDPOINTS.LOCATIONS.GET(userData.location_id), {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        if (response.data && response.data.name) {
          setLocationName(response.data.name);
          sessionStorage.setItem('location_name', response.data.name);
        } else {
          console.warn('handleLogin: Location response has no name field');
        }
      } catch (error) {
        console.error('handleLogin: Error fetching location name:', error);
        // Continue anyway, just won't show location name
      }
    }
  };

  // TIER 4: Show logout modal instead of immediately logging out
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // TIER 4: Perform actual logout with selected scope
  const performLogout = async (logoutScope, reason = null) => {
    // Guard flag to prevent infinite logout loop (when token is blacklisted)
    if (isLoggingOutRef.current) {
      console.log('Logout already in progress, skipping duplicate logout call');
      return;
    }
    isLoggingOutRef.current = true;
    setIsLoggingOut(true);

    // TIER 3: CRITICAL FIX - Don't set handleLogoutRef to null here
    // The guard flag isLoggingOutRef prevents duplicate logout calls
    // Setting it to null breaks polling's ability to call logout when session is detected as inactive
    // Instead, rely on the isLoggingOutRef guard to prevent infinite loops
    // Store logout reason for kicked-out page display ONLY if provided
    // If reason is null (explicit user logout), don't set kicked-out reason - just go to login page
    // If reason is provided (session closed, kicked out, etc.), show the kicked-out page
    if (reason) {
      setKickedOutReason(reason);
      sessionStorage.setItem('kicked_out_reason', reason);
    } else {
      // Explicit user logout - clear any previous kicked-out reason
      setKickedOutReason(null);
      sessionStorage.removeItem('kicked_out_reason');
    }

    try {
      // TIER 2/4: Call backend logout endpoint with logout_scope
      if (token) {
        // Token exists, making POST to /api/auth/logout
        const response = await axios.post(API_ENDPOINTS.AUTH.LOGOUT, {
          logout_scope: logoutScope,  // TIER 4: Pass logout scope
          device_id: `browser-${navigator.userAgent.substring(0, 50)}`
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        console.log('No token found - skipping backend logout call');
      }
    } catch (error) {
      console.error('Backend logout error:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      // Continue with frontend logout even if backend call fails
    }

    // TIER 1: Signal to other tabs - FOR BOTH 'device' AND 'all_devices' scopes
    // Device scope: Logout from all tabs on this device (same browser)
    // All devices scope: Logout from all tabs on all devices
    // IMPORTANT: Wrap in try-catch for incognito mode where localStorage is not available
    try {
      if (user && user.id) {
        const logoutSignalKey = `logout_signal_${user.id}`;
        localStorage.setItem(logoutSignalKey, Date.now().toString());
      }
    } catch (storageError) {
      console.warn('localStorage not available (incognito mode?), skipping Tier 1 signal:', storageError.message);
      // In incognito mode, localStorage is not available
      // Tier 3 polling will still detect the logout on other tabs
    }

    // Clear all auth-related storage
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('location_id');
      sessionStorage.removeItem('location_name');
      sessionStorage.removeItem('organization_name');
      sessionStorage.removeItem('session_created_at');
      sessionStorage.removeItem('browser_mode');
      // NOTE: Do NOT remove 'kicked_out_reason' here - we need it to persist for the kicked-out page display
      // It will be cleared when user logs back in (in handleLogin)

      // Clear entire sessionStorage as backup except kicked_out_reason
      const kickedOutReason = sessionStorage.getItem('kicked_out_reason');
      sessionStorage.clear();
      if (kickedOutReason) {
        sessionStorage.setItem('kicked_out_reason', kickedOutReason);
      }
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e.message);
    }

    setUser(null);
    setToken(null);
    setLocationId(null);
    setLocationName('');
    setOrganizationName('');
    setCurrentView('login');
    setShowLogoutModal(false);
    setIsLoggingOut(false);
    if (window.location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }

    // Reset logout flag for next login
    isLoggingOutRef.current = false;
  };

  const handleLogout = async (logoutScope = 'device', logoutReason = null) => {
    // For Tier 3 compatibility (when session polling triggers logout)
    // Accepts optional logoutScope and logoutReason parameters from polling
    // Default to 'device' scope for security
    await performLogout(logoutScope, logoutReason);
  };

  // Phase 1: Handle password reset requirement routing
  // This effect watches for password_reset_required flag and routes accordingly
  useEffect(() => {
    if (user && token) {
      if (user.password_reset_required === true || user.password_reset_required === 1) {
        // Only redirect if we're not already on the password-reset-required page
        // This prevents redirect loops when user clicks "Back to Login"
        const currentPath = window.location.pathname;
        if (currentPath !== '/password-reset-required') {
          // Use React Router navigate() to properly update the pathname and trigger re-render
          navigate('/password-reset-required');
        }
      }
    }
  }, [user?.password_reset_required, user?.id, token, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.STATS.ALL, getAuthHeaders());
      setStats(response.data);
      // Also fetch critical complaints
      fetchCriticalComplaints();
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchCriticalComplaints = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.COMPLAINTS.LIST, getAuthHeaders());
      // Filter and sort critical complaints (severity must be 'critical')
      const critical = response.data
        .filter(c => c.severity === 'critical' && ACTIVE_COMPLAINT_STATUSES.includes(c.status))
        .sort((a, b) => {
          // Sort by most recent first
          return new Date(b.created_at) - new Date(a.created_at);
        })
        .slice(0, 10); // Get top 10
      setCriticalComplaints(critical);
    } catch (error) {
      console.error('Error fetching critical complaints:', error);
      // Silent fail - don't disrupt dashboard
    }
  };

  const fetchAlertCount = async () => {
    try {
      // Phase 2 Production: Call /api/alerts endpoint with summary mode for lightweight response (~100 bytes)
      const response = await axios.get(API_ENDPOINTS.ALERTS.LIST + '?mode=summary', getAuthHeaders());

      const criticalCount = response.data.counts_by_severity?.critical || 0;
      const warningCount = response.data.counts_by_severity?.warning || 0;
      const infoCount = response.data.counts_by_severity?.info || 0;
      const totalAlerts = criticalCount + warningCount + infoCount;

      setAlertCount(totalAlerts); // Show all severity levels count in badge

      // Optional login summary — off by default; Preferences → Alerts
      if (totalAlerts > 0 && getLoginAlertsToast()) {
        const hasShownAlertNotification = sessionStorage.getItem('alert_notification_shown');
        if (!hasShownAlertNotification) {
          let message = `⚠️ You have ${totalAlerts} alert${totalAlerts !== 1 ? 's' : ''}`;
          if (criticalCount > 0) {
            message += ` (${criticalCount} critical)`;
          }
          toast.warning(message, { autoClose: 4000 });
          sessionStorage.setItem('alert_notification_shown', 'true');
        }
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Silent fail - don't disrupt user experience
    }
  };


  const handleProfilePictureUpdate = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.AUTH.ME, getAuthHeaders());
      const updatedUser = response.data;

      setUser(updatedUser);
      sessionStorage.setItem('user', JSON.stringify(updatedUser));

      if (updatedUser.location_name) {
        setLocationName(updatedUser.location_name);
        sessionStorage.setItem('location_name', updatedUser.location_name);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };


  const handleAlertDeviceEdit = async (device) => {
    // CRITICAL FIX: Use flushSync to ensure all form state is set BEFORE component renders
    // Also set viewingDeviceId to prevent useEffect (lines 600-623) from resetting the form state
    // This prevents race condition where Devices component mounts with undefined selectedDevice/showForm
    // then updates occur after, causing incorrect view (device list instead of edit form)
    flushSync(() => {
      setViewingDeviceId(device.id); // CRITICAL: Set this so useEffect doesn't reset form
      setSelectedDevice(device);
      setDeviceViewSource('alerts');
      setShowDeviceView(false); // Ensure device view is closed when opening form
      setShowForm(true);
    });

    // NOW change the view - form state is already committed
    navigateToView('devices');
  };

  const handleExportMaintenancePDF = async () => {
    if (!token) return;
    try {
      const { exportedRowCount } = await exportFilteredMaintenanceHistory({}, 'pdf', token);
      const count = exportedRowCount ?? 0;
      toast.success(
        count > 0
          ? `Exported ${count} maintenance record${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting maintenance PDF:', error);
      toast.error(error.message || 'Failed to export maintenance report');
    }
  };

  const handleExportComplaintsPDF = async () => {
    if (!token) return;
    try {
      const { exportedRowCount } = await exportFilteredComplaints({}, null, 'pdf', token);
      const count = exportedRowCount ?? 0;
      toast.success(
        count > 0
          ? `Exported ${count} complaint${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting complaints PDF:', error);
      toast.error(error.message || 'Failed to export complaints report');
    }
  };

  // Navigation handlers with filters
  const handleNavigateToDevicesWithFilter = (filterOptions = {}) => {
    // Navigate to devices view with optional filter options
    // Clear device view state to ensure we show device list, not detail view
    // (viewingDeviceId was probably set from previously viewed device)
    flushSync(() => {
      setViewingDeviceId(null);
      setShowDeviceView(false);
      setShowForm(false);
      setSelectedDevice(null);

      // Store the filter options to be applied when Devices component mounts
      if (filterOptions && Object.keys(filterOptions).length > 0) {
        // Pass filter options via state/context if provided
        window.deviceFilterOptions = filterOptions;
      }
    });

    // NOW change the view - device view state is already cleared
    navigateToView('devices');
  };

  const handleNavigateToMaintenanceHistory = (filterOptions) => {
    // Same pattern as Complaints: put device_identifier in search via React state + flushSync
    // so the page mounts with the filter already committed (avoids technician devices→assigned-devices race)
    let searchText = null;
    if (filterOptions) {
      if (typeof filterOptions === 'object') {
        const raw = filterOptions.search || filterOptions.device_identifier;
        searchText = typeof raw === 'string' && raw.trim() ? raw.trim() : null;
      } else if (typeof filterOptions === 'string' && filterOptions.trim()) {
        searchText = filterOptions.trim();
      }
    }

    flushSync(() => {
      setMaintenanceSearchFilter(searchText);
      setViewingDeviceId(null);
      setShowDeviceView(false);
      setShowForm(false);
      navigateToView('maintenance-history');
    });
  };

  const navigateToActivityLog = (preset = '7d') => {
    sessionStorage.setItem('activityLogPreset', preset);
    navigateToView('activity-log');
  };

  const handleNavigateToAlertsWithFilter = (filterOptions) => {
    // Navigate to alerts view with optional filter options
    // filterOptions can include device_identifier and subject_types
    if (filterOptions) {
      // Store filter options to be applied when Alerts component mounts
      window.alertFilterOptions = filterOptions;
    }
    navigateToView('alerts');
  };

  const handleNavigateToComplaintsWithFilter = (filterOptions) => {
    // Normalize filter options - handle both singular and plural forms, strings and arrays
    const getStatusesArray = () => {
      // Check for plural form (array) first - preferred format
      if (filterOptions.statuses && Array.isArray(filterOptions.statuses) && filterOptions.statuses.length > 0) {
        return filterOptions.statuses;
      }
      // Check for singular form (string)
      if (filterOptions.status) {
        if (Array.isArray(filterOptions.status)) {
          return filterOptions.status.length > 0 ? filterOptions.status : undefined;
        }
        // Handle comma-separated statuses (e.g., 'open,in_progress')
        const statuses = filterOptions.status.includes(',')
          ? filterOptions.status.split(',')
          : [filterOptions.status];
        return statuses;
      }
      return undefined;
    };

    const getSeveritiesArray = () => {
      // Check for plural form (array) first - preferred format
      if (filterOptions.severities && Array.isArray(filterOptions.severities) && filterOptions.severities.length > 0) {
        return filterOptions.severities;
      }
      // Check for singular form (string)
      if (filterOptions.severity) {
        if (Array.isArray(filterOptions.severity)) {
          return filterOptions.severity.length > 0 ? filterOptions.severity : undefined;
        }
        return [filterOptions.severity];
      }
      return undefined;
    };

    // Helper to normalize device IDs (support both singular and plural formats)
    const getDeviceIdsArray = () => {
      // Check for plural form (array) first - preferred format
      if (filterOptions.deviceIds && Array.isArray(filterOptions.deviceIds) && filterOptions.deviceIds.length > 0) {
        return filterOptions.deviceIds.map((id) => String(id).trim()).filter(Boolean);
      }
      // Check for singular form - backward compatibility
      if (filterOptions.deviceId) {
        const id = String(filterOptions.deviceId).trim();
        return id ? [id] : null;
      }
      return null;
    };

    const statuses = getStatusesArray();
    const severities = getSeveritiesArray();
    const deviceIds = getDeviceIdsArray();
    const normalizedDeviceIds = deviceIds && deviceIds.length > 0 ? deviceIds : null;
    const searchText =
      typeof filterOptions.search === 'string' && filterOptions.search.trim()
        ? filterOptions.search.trim()
        : null;

    // CRITICAL FIX: Use flushSync to ensure filters are set BEFORE component mounts
    // This prevents race condition where component mounts with old/undefined filter props
    // then filters are updated after, causing duplicate API calls with different filter states
    // ALSO: Clear viewingComplaintId to prevent showing old complaint view instead of complaints list
    flushSync(() => {
      // IMPORTANT: Clear the viewingComplaintId when navigating to complaints list
      // This prevents the issue where navigating from complaint detail view to dashboard
      // and then clicking a filter card would show the old complaint instead of the filtered list
      setViewingComplaintId(null);
      setShowDeviceView(false);
      setViewingDeviceId(null);
      setShowForm(false);
      setSelectedDevice(null);

      // Empty filter object {} or all filters undefined means show ALL
      if (
        Object.keys(filterOptions).length === 0 ||
        (statuses === undefined && severities === undefined && !normalizedDeviceIds && !searchText)
      ) {
        // Show all statuses (empty filter = all)
        setComplaintStatusFilter([]);
        setComplaintSeverityFilter(null);
        setComplaintDeviceIdsFilter(null);
        setComplaintSearchFilter(null);
      } else {
        // Set the complaint filters based on clicked card
        if (statuses) {
          setComplaintStatusFilter(statuses.map((s) => String(s).trim()).filter(Boolean));
        } else {
          // If status filter not provided, show all statuses
          setComplaintStatusFilter([]);
        }

        // Set severity filter if provided, otherwise clear it
        if (severities && severities.length > 0) {
          setComplaintSeverityFilter([...severities]);
        } else {
          // Clear severity filter if not provided (e.g., when navigating from Complaints Overview cards)
          setComplaintSeverityFilter(null);
        }

        // Set device IDs filter if provided, otherwise clear it
        if (normalizedDeviceIds) {
          setComplaintDeviceIdsFilter(normalizedDeviceIds);
        } else {
          // Clear device filter if not provided
          setComplaintDeviceIdsFilter(null);
        }

        // Search text (e.g. device identifier from Device View) — lives in search bar, not deviceIds filter
        setComplaintSearchFilter(searchText);
      }

      // Store days open filter if provided (for filtering complaints by age)
      // This is used for the "Pending >48h" filter in CentralHelpDeskDashboard
      // MUST be inside flushSync to ensure it's set before component mounts
      // ALWAYS update the state, whether provided or not, to clear previous filters
      setComplaintDaysOpenFromFilter(filterOptions.daysOpenFrom || '');
      setComplaintDaysOpenToFilter(filterOptions.daysOpenTo || '');

      // Change view inside flushSync so Complaints mounts with filters already committed
      navigateToView('complaints');
    });
  };

  // Check if this is a public/guest route (no authentication required)
  const pathname = window.location.pathname;

  // Phase 1: Handle email verification (no authentication required)
  if (pathname === '/verify-email') {
    return <VerifyEmail />;
  }

  // Legal / informational pages (no authentication required)
  if (pathname === '/privacy') {
    return <PrivacyPolicyPage />;
  }
  if (pathname === '/terms') {
    return <TermsOfServicePage />;
  }
  if (pathname === '/support') {
    return <SupportPage />;
  }

  // Phase 2: Handle account reactivation (no authentication required)
  if (pathname === '/reactivate') {
    return <ReactivateAccount />;
  }

  // Phase 3: Handle password reset (no authentication required)
  if (pathname === '/reset-password') {
    return <ResetPassword />;
  }

  // Phase 4: Handle QR device view - /device-scan?token={token}
  if (pathname === '/device-scan') {
    return (
      <>
        <QRDeviceViewHandler />
        <GlobalToastContainer />
      </>
    );
  }

  // Phase 5: Handle complaint confirmation - /complaint-confirmation
  if (pathname === '/complaint-confirmation') {
    return (
      <>
        <ComplaintConfirmationHandler />
        <GlobalToastContainer />
      </>
    );
  }

  // Phase 1: Check if this is the password reset required page
  // This must be BEFORE the "not authenticated" check because the user IS authenticated
  // They just need to set their password before accessing the main app
  if (pathname === '/password-reset-required') {
    // User must be authenticated to access this page
    if (user && token) {
      return <PasswordResetRequired user={user} token={token} />;
    } else {
      // If user tries to access this page without being authenticated, redirect to login
      return <Login onLogin={handleLogin} />;
    }
  }

  // Show kicked-out page if user was logged out with a reason
  // Note: kickedOutReason persists in sessionStorage even after user/token are cleared
  if (kickedOutReason) {
    return <KickedOutPage reason={kickedOutReason} loginUrl="/login" />;
  }

  // Show login if not authenticated and auth is initialized
  if (!user || !token) {
    if (pathToView(pathname)) {
      stashPostLoginViewFromPathname(pathname);
    } else if (pathname === '/' || pathname === '/login') {
      // Avoid applying a stale deep link from an earlier visit
      clearPostLoginView();
    }
    return <Login onLogin={handleLogin} />;
  }

  // SuperAdmin view
  if (user && user.role === 'superadmin') {
    return (
      <SuperAdminUserApp
        user={user}
        token={token}
        organizationName={organizationName}
        developerSettingsAvailable={developerSettingsAvailable}
        systemControlsAvailable={systemControlsAvailable}
        currentView={currentView}
        setCurrentView={navigateToView}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        showLogoutModal={showLogoutModal}
        setShowLogoutModal={setShowLogoutModal}
        isLoggingOut={isLoggingOut}
        performLogout={performLogout}
        handleLogoutClick={handleLogoutClick}
        onProfilePictureUpdate={handleProfilePictureUpdate}
        alertCount={alertCount}
        viewingDeviceId={viewingDeviceId}
        setViewingDeviceId={setViewingDeviceId}
        showDeviceView={showDeviceView}
        setShowDeviceView={setShowDeviceView}
        showForm={showForm}
        alertViewingDeviceId={alertViewingDeviceId}
        setAlertViewingDeviceId={setAlertViewingDeviceId}
        alertShowDeviceModal={alertShowDeviceModal}
        setAlertShowDeviceModal={setAlertShowDeviceModal}
        viewingComplaintId={viewingComplaintId}
        setViewingComplaintId={setViewingComplaintId}
        setShowForm={setShowForm}
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        deviceViewSource={deviceViewSource}
        setDeviceViewSource={setDeviceViewSource}
        handleAlertDeviceEdit={handleAlertDeviceEdit}
        handleNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
        handleNavigateToAlertsWithFilter={handleNavigateToAlertsWithFilter}
        handleNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
        handleNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
        navigateToUsers={navigateToUsers}
        usersStatusGroupFilter={usersStatusGroupFilter}
        setUsersStatusGroupFilter={setUsersStatusGroupFilter}
        usersLocationFilter={usersLocationFilter}
        setUsersLocationFilter={setUsersLocationFilter}
        fetchAlertCount={fetchAlertCount}
        navigateToLocationTransfers={navigateToLocationTransfers}
        navigateToDepartmentTransfers={navigateToDepartmentTransfers}
        complaintStatusFilter={complaintStatusFilter}
        setComplaintStatusFilter={setComplaintStatusFilter}
        complaintSeverityFilter={complaintSeverityFilter}
        setComplaintSeverityFilter={setComplaintSeverityFilter}
        complaintDaysOpenFromFilter={complaintDaysOpenFromFilter}
        setComplaintDaysOpenFromFilter={setComplaintDaysOpenFromFilter}
        complaintDaysOpenToFilter={complaintDaysOpenToFilter}
        setComplaintDaysOpenToFilter={setComplaintDaysOpenToFilter}
        complaintDeviceIdsFilter={complaintDeviceIdsFilter}
        setComplaintDeviceIdsFilter={setComplaintDeviceIdsFilter}
        complaintSearchFilter={complaintSearchFilter}
        setComplaintSearchFilter={setComplaintSearchFilter}
        maintenanceSearchFilter={maintenanceSearchFilter}
        setMaintenanceSearchFilter={setMaintenanceSearchFilter}
      />
    );
  }

  // Regular location user view - use RegularUserApp component to avoid hook ordering issues
  // Navigation handler to return to dashboard or approvals (for admins viewing pending approval devices)
  const handleReturnToDashboard = () => {
    setViewingDeviceId(null);
    setShowDeviceView(false);
    setShowForm(false);
    setSelectedDevice(null);
    // For admins, navigate to Device Approvals page when returning from a pending approval device
    // For other users, navigate to dashboard
    const isAdmin = user && (user.role === 'admin');
    const nextView = isAdmin ? 'approvals' : 'dashboard';
    navigateToView(nextView);
  };

  return (
    <RegularUserApp
      user={user}
      token={token}
      locationId={locationId}
      locationName={locationName}
      organizationName={organizationName}
      stats={stats}
      criticalComplaints={criticalComplaints}
      currentView={currentView}
      setCurrentView={navigateToView}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      alertCount={alertCount}
      viewingComplaintId={viewingComplaintId}
      setViewingComplaintId={setViewingComplaintId}
      viewingDeviceId={viewingDeviceId}
      setViewingDeviceId={setViewingDeviceId}
      showDeviceView={showDeviceView}
      setShowDeviceView={setShowDeviceView}
      showForm={showForm}
      setShowForm={setShowForm}
      selectedDevice={selectedDevice}
      setSelectedDevice={setSelectedDevice}
      deviceViewSource={deviceViewSource}
      setDeviceViewSource={setDeviceViewSource}
      complaintStatusFilter={complaintStatusFilter}
      setComplaintStatusFilter={setComplaintStatusFilter}
      complaintSeverityFilter={complaintSeverityFilter}
      setComplaintSeverityFilter={setComplaintSeverityFilter}
      complaintDaysOpenFromFilter={complaintDaysOpenFromFilter}
      setComplaintDaysOpenFromFilter={setComplaintDaysOpenFromFilter}
      complaintDaysOpenToFilter={complaintDaysOpenToFilter}
      setComplaintDaysOpenToFilter={setComplaintDaysOpenToFilter}
      complaintDeviceIdsFilter={complaintDeviceIdsFilter}
      setComplaintDeviceIdsFilter={setComplaintDeviceIdsFilter}
      complaintSearchFilter={complaintSearchFilter}
      setComplaintSearchFilter={setComplaintSearchFilter}
      maintenanceSearchFilter={maintenanceSearchFilter}
      setMaintenanceSearchFilter={setMaintenanceSearchFilter}
      alertViewingDeviceId={alertViewingDeviceId}
      setAlertViewingDeviceId={setAlertViewingDeviceId}
      alertShowDeviceModal={alertShowDeviceModal}
      setAlertShowDeviceModal={setAlertShowDeviceModal}
      showLogoutModal={showLogoutModal}
      setShowLogoutModal={setShowLogoutModal}
      isLoggingOut={isLoggingOut}
      performLogout={performLogout}
      handleLogoutClick={handleLogoutClick}
      handleProfilePictureUpdate={handleProfilePictureUpdate}
      handleAlertDeviceEdit={handleAlertDeviceEdit}
      handleExportMaintenancePDF={handleExportMaintenancePDF}
      handleExportComplaintsPDF={handleExportComplaintsPDF}
      handleNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
      handleNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
      handleNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
      handleNavigateToAlertsWithFilter={handleNavigateToAlertsWithFilter}
      navigateToActivityLog={navigateToActivityLog}
      handleReturnToDashboard={handleReturnToDashboard}
      fetchAlertCount={fetchAlertCount}
      navigateToLocationTransfers={navigateToLocationTransfers}
      navigateToDepartmentTransfers={navigateToDepartmentTransfers}
      navigateToSettings={navigateToSettings}
      navigateToUsers={navigateToUsers}
      usersStatusGroupFilter={usersStatusGroupFilter}
      setUsersStatusGroupFilter={setUsersStatusGroupFilter}
      usersLocationFilter={usersLocationFilter}
      setUsersLocationFilter={setUsersLocationFilter}
      settingsSection={settingsSection}
    />
  );
}

// SuperAdmin app component for superadmin role
function SuperAdminUserApp({
  user, token, organizationName, currentView, setCurrentView, mobileMenuOpen, setMobileMenuOpen,
  showLogoutModal, setShowLogoutModal, isLoggingOut, performLogout, handleLogoutClick, onProfilePictureUpdate,
  alertCount, viewingDeviceId, setViewingDeviceId, showDeviceView, setShowDeviceView, showForm, setShowForm,
  selectedDevice, setSelectedDevice, deviceViewSource, setDeviceViewSource, handleNavigateToMaintenanceHistory,
  handleNavigateToAlertsWithFilter, handleNavigateToComplaintsWithFilter, handleNavigateToDevicesWithFilter,
  navigateToUsers, usersStatusGroupFilter, setUsersStatusGroupFilter,
  usersLocationFilter, setUsersLocationFilter,
  fetchAlertCount,  alertViewingDeviceId, handleAlertDeviceEdit, alertShowDeviceModal,
  setAlertShowDeviceModal, setAlertViewingDeviceId, viewingComplaintId, setViewingComplaintId,
  developerSettingsAvailable, systemControlsAvailable, navigateToLocationTransfers, navigateToDepartmentTransfers,
  complaintStatusFilter, setComplaintStatusFilter, complaintSeverityFilter, setComplaintSeverityFilter,
  complaintDaysOpenFromFilter, setComplaintDaysOpenFromFilter, complaintDaysOpenToFilter, setComplaintDaysOpenToFilter,
  complaintDeviceIdsFilter, setComplaintDeviceIdsFilter,
  complaintSearchFilter, setComplaintSearchFilter, maintenanceSearchFilter, setMaintenanceSearchFilter
}) {
  const adminNavGroups = buildNavGroups(
    [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: '🏠',
          active: currentView === 'dashboard',
          onSelect: () => setCurrentView('dashboard'),
        },
        {
          id: 'devices',
          label: 'Devices',
          icon: '🖥️',
          active: currentView === 'devices',
          onSelect: () => {
            setViewingDeviceId(null);
            setShowDeviceView(false);
            setShowForm(false);
            setSelectedDevice(null);
            setCurrentView('devices');
          },
        },
        {
          id: 'complaints',
          label: 'Complaints',
          icon: '💬',
          active: currentView === 'complaints',
          onSelect: () => {
            // Same defaults as admin: null → preference open_tickets (hide closed/cancelled)
            setViewingComplaintId(null);
            setComplaintStatusFilter(null);
            setComplaintSeverityFilter(null);
            setComplaintDaysOpenFromFilter(null);
            setComplaintDaysOpenToFilter(null);
            setComplaintDeviceIdsFilter(null);
            setComplaintSearchFilter(null);
            setCurrentView('complaints');
          },
        },
        {
          id: 'analytics',
          label: 'Analytics',
          icon: '📈',
          active: currentView === 'analytics',
          onSelect: () => setCurrentView('analytics'),
        },
      ],
    [
        {
          id: 'users',
          label: 'Users',
          icon: '👥',
          active: currentView === 'users',
          onSelect: () => setCurrentView('users'),
        },
        {
          id: 'locations',
          label: 'Locations',
          icon: '📍',
          active: currentView === 'locations',
          onSelect: () => setCurrentView('locations'),
        },
        {
          id: 'departments',
          label: 'Departments',
          icon: '🏬',
          active: currentView === 'departments',
          onSelect: () => setCurrentView('departments'),
        },
        {
          id: 'device-names',
          label: 'Device Names',
          icon: '📇',
          active: currentView === 'device-names',
          onSelect: () => setCurrentView('device-names'),
        },
        {
          id: 'complaint-categories',
          label: 'Complaint Categories',
          icon: '📋',
          active: currentView === 'complaint-categories',
          onSelect: () => setCurrentView('complaint-categories'),
        },
        {
          id: 'data-import',
          label: 'Data Import',
          icon: '📥',
          active: currentView === 'data-import',
          onSelect: () => setCurrentView('data-import'),
        },
        {
          id: 'audit-viewer',
          label: 'Audit Trail',
          icon: '📜',
          active: currentView === 'audit-viewer',
          onSelect: () => setCurrentView('audit-viewer'),
        },
        {
          id: 'organization-settings',
          label: 'Organization',
          icon: '🎨',
          active: currentView === 'organization-settings',
          onSelect: () => setCurrentView('organization-settings'),
        },
        ...(systemControlsAvailable
          ? [{
              id: 'system-controls',
              label: 'System Controls',
              icon: '🎛️',
              active: currentView === 'system-controls',
              onSelect: () => setCurrentView('system-controls'),
            }]
          : []),
      ]
  );


  return (
    <AppShell
      role="superadmin"
      logo={
        <Logo size="medium" variant="light" organizationName={organizationName} isSuperAdmin={true} />
      }
      navGroups={adminNavGroups}
      user={user}
      currentView={currentView}
      alertCount={alertCount}
      showAlerts
      onAlertsClick={() => {
        setAlertViewingDeviceId(null);
        setAlertShowDeviceModal(false);
        setCurrentView('alerts');
      }}
      onProfileClick={() => setCurrentView('profile')}
      onPreferencesClick={() => setCurrentView('preferences')}
      onHelpClick={() => setCurrentView('help')}
      onLogout={handleLogoutClick}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
    >
        {currentView === 'dashboard' && (
          <SuperAdminDashboard
            token={token}
            user={user}
            onNavigateToDevices={(deviceId) => {
              setViewingDeviceId(deviceId);
              setShowDeviceView(true);
              setShowForm(false);
              setDeviceViewSource('dashboard');
              setCurrentView('devices');
            }}
            onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
            onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
            onNavigateToUsers={navigateToUsers}
            onNavigateToLocations={() => setCurrentView('locations')}
          />
        )}
        {currentView === 'users' && (
          <Users
            token={token}
            user={user}
            initialStatusGroup={usersStatusGroupFilter}
            initialLocations={usersLocationFilter}
            onInitialStatusGroupApplied={() => {
              setUsersStatusGroupFilter && setUsersStatusGroupFilter(null);
              setUsersLocationFilter && setUsersLocationFilter(null);
            }}
          />
        )}
        {currentView === 'locations' && (
          <LocationsManagement
            token={token}
            user={user}
            onViewAllDevicesAtLocation={(locationId) => {
              handleNavigateToDevicesWithFilter({
                locations: [String(locationId)],
              });
            }}
            onViewAllUsersAtLocation={(locationId) => {
              navigateToUsers(null, [String(locationId)]);
            }}
          />
        )}
        {currentView === 'departments' && (
          <DepartmentManagement token={token} />
        )}
        {currentView === 'device-names' && (
          <DeviceNameManagement token={token} />
        )}
        {currentView === 'complaint-categories' && (
          <ComplaintCategoryManagement token={token} />
        )}
        {currentView === 'analytics' && (
          <SuperAdminAnalytics token={token} />
        )}
        {currentView === 'data-import' && (
          <DataImportPage token={token} user={user} />
        )}
        {currentView === 'system-controls' && (
          <SystemControlsPage token={token} developerSettingsAvailable={developerSettingsAvailable} />
        )}
        {currentView === 'organization-settings' && (
          <OrganizationSettingsPage token={token} user={user} />
        )}
        {currentView === 'profile' && (
          <ProfileSettings
            token={token}
            user={user}
            onProfilePictureUpdate={onProfilePictureUpdate}
          />
        )}
        {currentView === 'preferences' && (
          <PreferencesPage user={user} />
        )}
        {currentView === 'help' && (
          <HelpPage user={user} />
        )}
        {currentView === 'audit-viewer' && (
          <AuditViewerPage token={token} user={user} locationId={null} />
        )}
        {currentView === 'devices' && (
          <Devices
            token={token}
            user={user}
            locationId={null}
            viewingDeviceId={viewingDeviceId}
            showDeviceView={showDeviceView}
            showForm={showForm}
            selectedDevice={selectedDevice}
            deviceViewSource={deviceViewSource}
            setViewingDeviceId={setViewingDeviceId}
            setShowDeviceView={setShowDeviceView}
            setShowForm={setShowForm}
            setSelectedDevice={setSelectedDevice}
            setDeviceViewSource={setDeviceViewSource}
            onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
            onNavigateToAlertsWithFilter={handleNavigateToAlertsWithFilter}
            onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
            onReturnToDashboard={() => {
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setCurrentView('dashboard');
            }}
            onReturnToDashboardFromDeviceView={() => {
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setDeviceViewSource('devices');
              setCurrentView('dashboard');
            }}
            onNavigateToLocationTransfers={navigateToLocationTransfers}
            onNavigateToDepartmentTransfers={navigateToDepartmentTransfers}
          />
        )}
        {currentView === 'complaints' && (
          <Complaints
            token={token}
            initialComplaintId={viewingComplaintId}
            onComplaintClose={() => {
              setViewingComplaintId(null);
              setCurrentView('dashboard');
            }}
            initialStatusFilter={complaintStatusFilter}
            initialSeverityFilter={complaintSeverityFilter}
            initialDaysOpenFrom={complaintDaysOpenFromFilter}
            initialDaysOpenTo={complaintDaysOpenToFilter}
            initialDeviceFilter={complaintDeviceIdsFilter}
            initialSearchFilter={complaintSearchFilter}
            onComplaintViewOpen={(complaintId) => setViewingComplaintId(complaintId)}
            user={user}
            onViewFullDeviceDetails={(deviceInfo) => {
              // Handle both object {id, serial_number} and direct id
              const deviceId = typeof deviceInfo === 'object' ? deviceInfo.id : deviceInfo;
              setViewingDeviceId(deviceId);
              // Set deviceViewSource to 'complaints' so back button returns to complaints view
              setDeviceViewSource('complaints');
              setShowDeviceView(true);
              setCurrentView('devices');
            }}
          />
        )}

        {currentView === 'maintenance-history' && (
          <DeviceMaintenanceHistory
            token={token}
            user={user}
            initialSearchFilter={maintenanceSearchFilter}
            onSearchFilterChange={setMaintenanceSearchFilter}
          />
        )}

        {currentView === 'alerts' && (
         <>
            <Alerts
              token={token}
              onDeviceEdit={handleAlertDeviceEdit}
              user={user}
              onRefresh={fetchAlertCount}
              onNavigateToApprovals={() => setCurrentView('approvals')}
              onNavigateToLocationTransfers={navigateToLocationTransfers}
              onNavigateToDepartmentTransfers={navigateToDepartmentTransfers}
              onNavigateToComplaintDetail={(complaintId) => {
                setViewingComplaintId(complaintId);
                setCurrentView('complaints');
              }}
              viewingDeviceId={alertViewingDeviceId}
              setViewingDeviceId={setAlertViewingDeviceId}
              showDeviceModal={alertShowDeviceModal}
              setShowDeviceModal={setAlertShowDeviceModal}
            />
          </>
        )}

      {showLogoutModal && (
        <LogoutModal
          onConfirm={performLogout}
          onCancel={() => setShowLogoutModal(false)}
          isLoading={isLoggingOut}
        />
      )}

      <GlobalToastContainer />
    </AppShell>
  );
}

// Regular user app component for non-superadmin users
function RegularUserApp({
  user, token, locationId, locationName, organizationName, stats,
  criticalComplaints, currentView, setCurrentView, mobileMenuOpen, setMobileMenuOpen, alertCount,
  viewingComplaintId, setViewingComplaintId,
  viewingDeviceId, setViewingDeviceId, showDeviceView, setShowDeviceView, showForm, setShowForm,
  selectedDevice, setSelectedDevice, deviceViewSource, setDeviceViewSource,
  complaintStatusFilter, setComplaintStatusFilter, complaintSeverityFilter, setComplaintSeverityFilter,
  complaintDaysOpenFromFilter, setComplaintDaysOpenFromFilter, complaintDaysOpenToFilter, setComplaintDaysOpenToFilter,
  complaintDeviceIdsFilter, setComplaintDeviceIdsFilter,
  complaintSearchFilter, setComplaintSearchFilter,
  maintenanceSearchFilter, setMaintenanceSearchFilter,
  usersStatusGroupFilter, setUsersStatusGroupFilter,
  usersLocationFilter, setUsersLocationFilter,
  alertViewingDeviceId, setAlertViewingDeviceId, alertShowDeviceModal, setAlertShowDeviceModal,
  showLogoutModal, setShowLogoutModal, isLoggingOut, performLogout, handleLogoutClick,
  handleProfilePictureUpdate, handleAlertDeviceEdit, handleExportMaintenancePDF,
  handleExportComplaintsPDF,   handleNavigateToDevicesWithFilter, handleNavigateToComplaintsWithFilter,   handleNavigateToMaintenanceHistory, handleNavigateToAlertsWithFilter,
  handleReturnToDashboard, fetchAlertCount, navigateToLocationTransfers, navigateToDepartmentTransfers, navigateToSettings, navigateToUsers, settingsSection,
  navigateToActivityLog,
}) {
  const { canAccessModule } = useRoleAccess(user);
  const [approvalCount, setApprovalCount] = useState(0);
  const [locationTransferCount, setLocationTransferCount] = useState(0);
  const [departmentTransferCount, setDepartmentTransferCount] = useState(0);

  const fetchActionNavCounts = useCallback(async () => {
    if (!token || !user?.role) return;
    const headers = { Authorization: `Bearer ${token}` };
    const role = user.role;

    if (role === 'admin') {
      try {
        const res = await axios.get(API_ENDPOINTS.APPROVALS.COUNTS, { headers });
        setApprovalCount(res.data?.pending_count || 0);
      } catch {
        setApprovalCount(0);
      }
    } else {
      setApprovalCount(0);
    }

    if (['admin', 'manager', 'technician'].includes(role)) {
      try {
        const res = await axios.get(API_ENDPOINTS.LOCATION_TRANSFERS.ACTION_SUMMARY, { headers });
        setLocationTransferCount(res.data?.total || 0);
      } catch {
        setLocationTransferCount(0);
      }
    } else {
      setLocationTransferCount(0);
    }

    if (role === 'custodian') {
      try {
        const res = await axios.get(API_ENDPOINTS.DEPARTMENT_TRANSFERS.ACTION_SUMMARY, { headers });
        setDepartmentTransferCount(res.data?.total || 0);
      } catch {
        setDepartmentTransferCount(0);
      }
    } else {
      setDepartmentTransferCount(0);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchActionNavCounts();
  }, [fetchActionNavCounts, currentView]);

  // Technicians use My Devices, not Device List — redirect if they land on the bare list
  useEffect(() => {
    if (
      user?.role === 'technician' &&
      currentView === 'devices' &&
      !showDeviceView &&
      !showForm
    ) {
      setCurrentView('assigned-devices', { replace: true });
      setDeviceViewSource('devices');
    }
  }, [user?.role, currentView, showDeviceView, showForm, setCurrentView, setDeviceViewSource]);

  // Check if user is department-scoped (has fixed department assignment)
  // NOTE: admin, manager, and central_help_desk are NOT department-scoped (can see all departments)
  const isDepartmentScoped = ['coordinator', 'custodian', 'technician'].includes(user?.role);

  // Check if user is admin (has access to all departments)
  // Central Help Desk also has access to all departments
  const isAdmin = user?.role === 'admin';

  // Admin cluster — same Work/Admin split as superadmin; omit group if role has no admin items
  const showUsersNav = canAccessModule('users', true);
  const showDataImportNav = user?.role === 'admin';
  const showAuditNav = canAccessModule('audit_viewer');
  const showSettingsNav = canAccessModule('settings');
  const showAdminNav = showUsersNav || showDataImportNav || showAuditNav || showSettingsNav;

  // Navbar: 1 dept = name; 2+ = compact label (full list lives on dashboard banner)
  const getDepartmentDisplay = () => {
    if (isAdmin || user?.role === 'manager' || user?.role === 'central_help_desk') {
      return 'All Departments';
    }
    const depts = user?.departments;
    if (!depts || depts.length === 0) {
      return null;
    }
    if (depts.length === 1) {
      return depts[0].name;
    }
    return 'Multiple Departments';
  };
  const getDepartmentIcon = () => {
    // Admin, Manager, and Central Help Desk can see all departments
    if (isAdmin || user?.role === 'manager' || user?.role === 'central_help_desk') {
      return '🌐';
    }
    if (user?.departments && user.departments.length > 0) {
      if (user.departments.length > 1) {
        return '🏢';
      }
      return user.departments[0].icon || '🏢';
    }
    return '🏢';
  };
  const departmentName = getDepartmentDisplay();
  const departmentIcon = getDepartmentIcon();
  const showDepartmentContext = isDepartmentScoped || isAdmin || user?.role === 'manager' || user?.role === 'central_help_desk';

  const workItems = [];
  if (canAccessModule('dashboard')) {
    workItems.push({
      id: 'dashboard',
      label: 'Dashboard',
      icon: '🏠',
      active: currentView === 'dashboard',
      onSelect: () => setCurrentView('dashboard'),
    });
  }
  if (canAccessModule('devices')) {
    workItems.push({
      id: 'devices',
      label: 'Devices',
      icon: '🖥️',
      active: currentView === 'devices',
      onSelect: () => {
        setViewingDeviceId(null);
        setShowDeviceView(false);
        setShowForm(false);
        setSelectedDevice(null);
        setCurrentView('devices');
      },
    });
  }
  if (canAccessModule('complaints')) {
    workItems.push({
      id: 'complaints',
      label: 'Complaints',
      icon: '💬',
      active: currentView === 'complaints',
      onSelect: () => {
        setViewingComplaintId(null);
        setComplaintStatusFilter(null);
        setComplaintSeverityFilter(null);
        setComplaintDaysOpenFromFilter(null);
        setComplaintDaysOpenToFilter(null);
        setComplaintDeviceIdsFilter(null);
        setComplaintSearchFilter(null);
        setCurrentView('complaints');
      },
    });
  }
  if (user?.role === 'technician') {
    workItems.push({
      id: 'assigned-devices',
      label: 'My Devices',
      icon: '🖥️',
      active: currentView === 'assigned-devices',
      onSelect: () => setCurrentView('assigned-devices'),
    });
    workItems.push({
      id: 'work-logs',
      label: 'My Work Logs',
      icon: '📋',
      active: currentView === 'work-logs',
      onSelect: () => setCurrentView('work-logs'),
    });
  }
  if (canAccessModule('devices') && ['admin', 'manager', 'coordinator', 'custodian', 'central_help_desk'].includes(user?.role)) {
    workItems.push({
      id: 'maintenance-history',
      label: 'Maintenance',
      icon: '🛠️',
      active: currentView === 'maintenance-history',
      onSelect: () => {
        setMaintenanceSearchFilter(null);
        setViewingDeviceId(null);
        setShowDeviceView(false);
        setCurrentView('maintenance-history');
      },
    });
  }
  if (user?.role === 'admin') {
    workItems.push({
      id: 'approvals',
      label: 'Approvals',
      icon: '✅',
      badge: approvalCount,
      active: currentView === 'approvals',
      onSelect: () => setCurrentView('approvals'),
    });
  }
  if (user && ['admin', 'manager', 'technician'].includes(user.role)) {
    workItems.push({
      id: 'location-transfers',
      label: 'Location Transfers',
      icon: '🚚',
      badge: locationTransferCount,
      active: currentView === 'location-transfers',
      onSelect: () => setCurrentView('location-transfers'),
    });
  }
  if (user?.role === 'custodian') {
    workItems.push({
      id: 'department-transfers',
      label: 'Department Transfers',
      icon: '🔁',
      badge: departmentTransferCount,
      active: currentView === 'department-transfers',
      onSelect: () => setCurrentView('department-transfers'),
    });
  }
  if (user && ['admin', 'manager', 'coordinator', 'custodian'].includes(user.role)) {
    workItems.push({
      id: 'activity-log',
      label: 'Activity Log',
      icon: '📋',
      active: currentView === 'activity-log',
      onSelect: () => setCurrentView('activity-log'),
    });
  }
  if (canAccessModule('analytics')) {
    workItems.push({
      id: 'analytics',
      label: 'Analytics',
      icon: '💡',
      active: currentView === 'analytics',
      onSelect: () => setCurrentView('analytics'),
    });
  }

  const adminItems = [];
  if (showUsersNav) {
    adminItems.push({
      id: 'users',
      label: 'Users',
      icon: '👥',
      active: currentView === 'users',
      onSelect: () => {
        setUsersStatusGroupFilter(null);
        setCurrentView('users');
      },
    });
  }
  if (showDataImportNav) {
    adminItems.push({
      id: 'data-import',
      label: 'Data Import',
      icon: '📥',
      active: currentView === 'data-import',
      onSelect: () => setCurrentView('data-import'),
    });
  }
  if (showAuditNav) {
    adminItems.push({
      id: 'audit-viewer',
      label: 'Audit Trail',
      icon: '📜',
      active: currentView === 'audit-viewer',
      onSelect: () => setCurrentView('audit-viewer'),
    });
  }
  if (showSettingsNav) {
    adminItems.push({
      id: 'settings',
      label: 'Settings',
      icon: '⚙️',
      active: currentView === 'settings',
      onSelect: () => navigateToSettings(),
    });
  }

  const navGroups = buildNavGroups(
    workItems,
    showAdminNav ? adminItems : []
  );

  return (
    <AppShell
      role={user?.role || 'guest'}
      logo={
        <Logo
          size="medium"
          variant="light"
          organizationName={organizationName}
          locationName={locationName}
          departmentName={showDepartmentContext ? departmentName : null}
          departmentIcon={showDepartmentContext ? departmentIcon : null}
        />
      }
      navGroups={navGroups}
      user={user}
      currentView={currentView}
      alertCount={alertCount}
      showAlerts={canAccessModule('alerts')}
      onAlertsClick={() => {
        setAlertViewingDeviceId(null);
        setAlertShowDeviceModal(false);
        setCurrentView('alerts');
      }}
      onProfileClick={() => setCurrentView('profile')}
      onPreferencesClick={() => setCurrentView('preferences')}
      onHelpClick={() => setCurrentView('help')}
      onLogout={handleLogoutClick}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
    >
        {currentView === 'dashboard' && (
          <>
            {/* NEW MODULAR DASHBOARD - Admin */}
            {user?.role === 'admin' && (
              <AdminDashboard
                token={token}
                user={user}
                onNavigateToApprovals={() => setCurrentView('approvals')}
                onNavigateToSettings={navigateToSettings}
                onNavigateToLocationTransfers={navigateToLocationTransfers}
            onNavigateToDepartmentTransfers={navigateToDepartmentTransfers}
                onNavigateToUsers={navigateToUsers}
                onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
                onNavigateToDevices={(deviceId) => {
                  setViewingDeviceId(deviceId);
                  setShowDeviceView(true);
                  setShowForm(false);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
              />
            )}

            {/* NEW MODULAR DASHBOARD - Technician only */}
            {user?.role === 'technician' && (
              <TechnicianDashboard
                token={token}
                user={user}
                onNavigateToDevices={() => {
                  // Navigate to My Devices (assigned-devices view) for Technician
                  setCurrentView('assigned-devices');
                }}
                onNavigateToAlerts={() => setCurrentView('alerts')}
                onNavigateToComplaints={(complaintId) => {
                  setViewingComplaintId(complaintId);
                  setCurrentView('complaints');
                }}
                onNavigateToWorkLogs={() => setCurrentView('work-logs')}
                onNavigateToDeviceView={(deviceId) => {
                  setViewingDeviceId(deviceId);
                  setShowDeviceView(true);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
              />
            )}

            {/* NEW MODULAR DASHBOARD - Manager only */}
            {user?.role === 'manager' && (
              <ManagerDashboard
                token={token}
                user={user}
                onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
                onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
                onNavigateToActivityLog={() => navigateToActivityLog('7d')}
                onNavigateToDeviceView={(deviceId) => {
                  setViewingDeviceId(deviceId);
                  setShowDeviceView(true);
                  setShowForm(false);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
                onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
                onNavigateToAlerts={() => setCurrentView('alerts')}
                onEditDeviceFromApprovals={(device) => {
                  // Edit device from approvals table
                  setViewingDeviceId(device.id);
                  setSelectedDevice(device);
                  setShowForm(true);
                  setShowDeviceView(false);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
              />
            )}

            {/* NEW MODULAR DASHBOARD - Central Help Desk only */}
            {user?.role === 'central_help_desk' && (
              <CentralHelpDeskDashboard
                token={token}
                user={user}
                onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
                onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
              />
            )}

            {/* NEW MODULAR DASHBOARD - Custodian only */}
            {user?.role === 'custodian' && (
              <CustodianDashboard
                token={token}
                user={user}
                onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
                onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
                onNavigateToActivityLog={() => navigateToActivityLog('7d')}
                onNavigateToDeviceView={(deviceId) => {
                  setViewingDeviceId(deviceId);
                  setShowDeviceView(true);
                  setShowForm(false);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
                onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
              />
            )}

            {/* NEW MODULAR DASHBOARD - Coordinator only */}
            {user?.role === 'coordinator' && (
              <CoordinatorDashboard
                token={token}
                user={user}
                onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
                onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
                onNavigateToActivityLog={() => navigateToActivityLog('7d')}
                onNavigateToDeviceView={(deviceId) => {
                  setViewingDeviceId(deviceId);
                  setShowDeviceView(true);
                  setShowForm(false);
                  setDeviceViewSource('dashboard');
                  setCurrentView('devices');
                }}
                onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
              />
            )}

            {/* Show loading state while stats are being fetched for non-Technician, non-Manager, non-Custodian, non-Coordinator, non-CentralHelpDesk, non-Admin roles */}
            {user?.role !== 'technician' && user?.role !== 'manager' && user?.role !== 'custodian' && user?.role !== 'coordinator' && user?.role !== 'central_help_desk' && user?.role !== 'admin' && !stats && (
              <div className="dashboard-loading">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading dashboard...</p>
                </div>
              </div>
            )}
          </>
        )}

        {currentView === 'complaints' && (
          <Complaints
            token={token}
            initialComplaintId={viewingComplaintId}
            onComplaintClose={() => {
              setViewingComplaintId(null);
              // For Technicians, also return to dashboard instead of showing complaints list
              if (user?.role === 'technician') {
                setCurrentView('dashboard');
              }
            }}
            initialStatusFilter={complaintStatusFilter}
            initialSeverityFilter={complaintSeverityFilter}
            initialDaysOpenFrom={complaintDaysOpenFromFilter}
            initialDaysOpenTo={complaintDaysOpenToFilter}
            initialDeviceFilter={complaintDeviceIdsFilter}
            initialSearchFilter={complaintSearchFilter}
            onViewFullDeviceDetails={(deviceInfo) => {
              // Handle both object {id, serial_number} and direct id
              const deviceId = typeof deviceInfo === 'object' ? deviceInfo.id : deviceInfo;
              setViewingDeviceId(deviceId);
              // Set deviceViewSource to 'complaints' so back button returns to complaints view
              setDeviceViewSource('complaints');
              setShowDeviceView(true);
              setCurrentView('devices');
            }}
            onComplaintViewOpen={(complaintId) => {
              // Keep parent App.js state in sync with which complaint is being viewed in Complaints component
              setViewingComplaintId(complaintId);
            }}
            user={user}
          />
        )}

        {currentView === 'analytics' && (
          user?.role === 'technician' ? (
            <TechnicianAnalytics
              token={token}
              user={user}
              onNavigateToAlerts={() => setCurrentView('alerts')}
              onNavigateToDevices={() => setCurrentView('assigned-devices')}
              onNavigateToDashboard={() => setCurrentView('dashboard')}
              onNavigateToDeviceView={(deviceId) => {
                setViewingDeviceId(deviceId);
                setShowDeviceView(true);
                setShowForm(false);
                setDeviceViewSource('analytics');
                setCurrentView('devices');
              }}
            />
          ) : user?.role === 'superadmin' ? (
            <SuperAdminAnalytics
              token={token}
              user={user}
              onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
              onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
              onNavigateToDeviceView={(deviceId) => {
                setViewingDeviceId(deviceId);
                setShowDeviceView(true);
                setShowForm(false);
                setDeviceViewSource('analytics');
                setCurrentView('devices');
              }}
            />
          ) : (
            <AdminAnalytics
              token={token}
              user={user}
              onNavigateToDevicesWithFilter={handleNavigateToDevicesWithFilter}
              onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
              onNavigateToDeviceView={(deviceId) => {
                setViewingDeviceId(deviceId);
                setShowDeviceView(true);
                setShowForm(false);
                setDeviceViewSource('analytics');
                setCurrentView('devices');
              }}
            />
          )
        )}

        {currentView === 'alerts' && (
          <Alerts
            token={token}
            onDeviceEdit={handleAlertDeviceEdit}
            user={user}
            onRefresh={fetchAlertCount}
            onNavigateToApprovals={() => setCurrentView('approvals')}
            onNavigateToLocationTransfers={navigateToLocationTransfers}
            onNavigateToDepartmentTransfers={navigateToDepartmentTransfers}
            onNavigateToComplaintDetail={(complaintId) => {
              setViewingComplaintId(complaintId);
              setCurrentView('complaints');
            }}
            viewingDeviceId={alertViewingDeviceId}
            setViewingDeviceId={setAlertViewingDeviceId}
            showDeviceModal={alertShowDeviceModal}
            setShowDeviceModal={setAlertShowDeviceModal}
          />
        )}

        {currentView === 'assigned-devices' && (
          <AssignedDevices
            token={token}
            user={user}
            onViewDevice={(deviceId) => {
              setViewingDeviceId(deviceId);
              setShowDeviceView(true);
              setCurrentView('devices');
            }}
            onSourceChange={(source) => {
              setDeviceViewSource(source);
            }}
          />
        )}

        {currentView === 'work-logs' && (
          <MyWorkLogs token={token} user={user} />
        )}

        {currentView === 'activity-log' && (
          <ActivityLogPage
            token={token}
            user={user}
            onNavigateToDeviceView={(deviceId) => {
              setViewingDeviceId(deviceId);
              setShowDeviceView(true);
              setShowForm(false);
              setDeviceViewSource('activity-log');
              setCurrentView('devices');
            }}
            onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
          />
        )}

        {currentView === 'maintenance-history' && (
          <DeviceMaintenanceHistory
            token={token}
            user={user}
            initialSearchFilter={maintenanceSearchFilter}
            onSearchFilterChange={setMaintenanceSearchFilter}
          />
        )}

        {currentView === 'devices' && (
          <Devices
            token={token}
            user={user}
            locationId={locationId}
            onNavigateToComplaintDetail={(complaintId) => {
              setViewingComplaintId(complaintId);
              setCurrentView('complaints');
            }}
            onNavigateToComplaintsWithFilter={handleNavigateToComplaintsWithFilter}
            onNavigateToMaintenanceHistory={handleNavigateToMaintenanceHistory}
            onNavigateToAlertsWithFilter={handleNavigateToAlertsWithFilter}
            viewingDeviceId={viewingDeviceId}
            showDeviceView={showDeviceView}
            showForm={showForm}
            selectedDevice={selectedDevice}
            deviceViewSource={deviceViewSource}
            setViewingDeviceId={setViewingDeviceId}
            setShowDeviceView={setShowDeviceView}
            setShowForm={setShowForm}
            setSelectedDevice={setSelectedDevice}
            setDeviceViewSource={setDeviceViewSource}
            onReturnToDashboard={handleReturnToDashboard}
            onNavigateToLocationTransfers={navigateToLocationTransfers}
            onNavigateToDepartmentTransfers={navigateToDepartmentTransfers}
            onReturnToComplaints={() => {
              // Navigate back to complaints view when device view is closed from complaints
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setCurrentView('complaints');
            }}
            onReturnToAssignedDevices={() => {
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setDeviceViewSource('devices');
              setCurrentView('assigned-devices');
            }}
            onReturnToDashboardFromDeviceView={() => {
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setDeviceViewSource('devices');
              setCurrentView('dashboard');
            }}
            onReturnToAnalytics={() => {
              setViewingDeviceId(null);
              setShowDeviceView(false);
              setShowForm(false);
              setSelectedDevice(null);
              setDeviceViewSource('devices');
              setCurrentView('analytics');
            }}
          />
        )}

        {currentView === 'approvals' && user?.role === 'admin' && (
          <DeviceApprovals token={token} user={user} onActionCountsChange={fetchActionNavCounts} />
        )}

        {currentView === 'location-transfers' && user && ['admin', 'manager', 'technician'].includes(user.role) && (
          <LocationTransfersPage
            token={token}
            user={user}
            onActionCountsChange={fetchActionNavCounts}
          />
        )}

        {currentView === 'department-transfers' && user?.role === 'custodian' && (
          <DepartmentTransfersPage
            token={token}
            user={user}
            onActionCountsChange={fetchActionNavCounts}
          />
        )}

        {currentView === 'users' && user && (user.role === 'admin') && (
          <Users
            token={token}
            user={user}
            initialStatusGroup={usersStatusGroupFilter}
            initialLocations={usersLocationFilter}
            onInitialStatusGroupApplied={() => {
              setUsersStatusGroupFilter(null);
              setUsersLocationFilter && setUsersLocationFilter(null);
            }}
          />
        )}

        {currentView === 'settings' && user && user.role === 'admin' && locationId && (
          <SettingsPage
            token={token}
            locationId={locationId}
            user={user}
            initialExpandedCard={settingsSection}
          />
        )}
        {currentView === 'audit-viewer' && user && (user.role === 'admin' || user.role === 'superadmin') && (
          <AuditViewerPage token={token} user={user} locationId={locationId} />
        )}

        {currentView === 'profile' && (
          <ProfileSettings
            token={token}
            user={user}
            onProfilePictureUpdate={handleProfilePictureUpdate}
          />
        )}

        {currentView === 'preferences' && (
          <PreferencesPage user={user} />
        )}

        {currentView === 'help' && (
          <HelpPage user={user} />
        )}

        {currentView === 'data-import' && user?.role === 'admin' && (
          <DataImportPage token={token} user={user} />
        )}

      {/* TIER 4: Logout Modal - Ask user where to logout from */}
      {showLogoutModal && (
        <LogoutModal
          onConfirm={performLogout}
          onCancel={() => setShowLogoutModal(false)}
          isLoading={isLoggingOut}
        />
      )}

      <GlobalToastContainer />
    </AppShell>
  );
}

// Wrapper component that provides AuthContext to the entire app
function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;
